import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity,
  Check,
  Copy,
  Eye,
  EyeOff,
  Plus,
  Shield,
  Trash2,
  User,
  Pencil,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import { IProxy, IAccount } from '@tool-no-hu/shared/types';
import { useProxies } from '../../hooks/useProxies';
import { DataToolbar } from '../../components/common/DataToolbar';
import { DataStateMessage } from '../../components/common/DataStateMessage';

// ─── Helpers ────────────────────────────────────────────────────────────────

function maskProxy(value: string): string {
  const parts = value.split(':');
  if (parts.length < 3) return value;
  return `${parts[0]}:${parts[1]}:****:****`;
}

function formatTime(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = new Date(date);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Vài giây trước';
  if (mins < 60) return `${mins} phút trước`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} giờ trước`;
  return d.toLocaleDateString('vi-VN');
}

// ─── Proxy Add Popup ────────────────────────────────────────────────────────

interface ProxyAddPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: string[]) => Promise<boolean>;
}

const ProxyAddPopup: React.FC<ProxyAddPopupProps> = ({ isOpen, onClose, onSubmit }) => {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return;
    setSaving(true);
    try {
      const ok = await onSubmit(lines);
      if (ok) {
        setText('');
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/60" onClick={onClose} />
          <motion.div
            className="relative w-full max-w-[520px] rounded-xl border border-[#252a38] bg-[#161920] shadow-2xl"
            initial={{ scale: 0.95, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 10 }}
            transition={{ duration: 0.15 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[#252a38] px-5 py-4">
              <h3 className="text-[14px] font-bold text-[#e2e8f0]">Thêm Proxy</h3>
              <button
                onClick={onClose}
                disabled={saving}
                className="rounded-lg p-1.5 text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0] disabled:opacity-50"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-5 pt-5">
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                Nhập proxy (mỗi dòng một proxy)
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="host:port:user:pass&#10;host:port&#10;103.69.96.15:7777:user:pass"
                rows={8}
                className="w-full rounded-lg border border-[#252a38] bg-[#0d0f14] px-3.5 py-2.5 font-mono text-[13px] text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6] resize-none"
              />
              <p className="mt-2 text-[11px] text-[#475569]">
                Hỗ trợ định dạng:{' '}
                <code className="rounded bg-[#1e2235] px-1.5 py-0.5 font-mono text-[#7dd3fc]">host:port:user:pass</code>{' '}
                hoặc{' '}
                <code className="rounded bg-[#1e2235] px-1.5 py-0.5 font-mono text-[#7dd3fc]">host:port</code>
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-[#252a38] px-5 py-4 mt-5">
              <button
                onClick={onClose}
                disabled={saving}
                className="rounded-lg border border-[#252a38] px-4 py-2 text-[12px] font-bold text-[#94a3b8] transition-colors hover:bg-[#1e2235] disabled:opacity-50"
              >
                Hủy
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || !text.trim()}
                className="flex items-center gap-1.5 rounded-lg bg-[#3b82f6] px-5 py-2 text-[12px] font-bold text-white transition-colors hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                {saving ? 'Đang thêm...' : 'Thêm proxy'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ─── Proxy Edit Popup ───────────────────────────────────────────────────────

interface ProxyEditPopupProps {
  isOpen: boolean;
  onClose: () => void;
  proxy: IProxy;
  onSave: (id: string, value: string) => Promise<boolean>;
}

const ProxyEditPopup: React.FC<ProxyEditPopupProps> = ({ isOpen, onClose, proxy, onSave }) => {
  const [value, setValue] = useState(proxy.value);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(proxy.value);
  }, [proxy.value]);

  const handleSave = async () => {
    if (!value.trim() || saving || !proxy._id) return;
    setSaving(true);
    try {
      const ok = await onSave(proxy._id, value.trim());
      if (ok) onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/60" onClick={onClose} />
          <motion.div
            className="relative w-full max-w-[480px] rounded-xl border border-[#252a38] bg-[#161920] shadow-2xl"
            initial={{ scale: 0.95, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 10 }}
            transition={{ duration: 0.15 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[#252a38] px-5 py-4">
              <h3 className="text-[14px] font-bold text-[#e2e8f0]">
                Sửa Proxy: <span className="text-[#3b82f6]">{maskProxy(proxy.value)}</span>
              </h3>
              <button
                onClick={onClose}
                disabled={saving}
                className="rounded-lg p-1.5 text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0] disabled:opacity-50"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-5 pt-5">
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                Giá trị proxy
              </label>
              <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="host:port:user:pass"
                className="w-full rounded-lg border border-[#252a38] bg-[#0d0f14] px-3.5 py-2.5 font-mono text-[13px] text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6]"
              />
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-[#252a38] px-5 py-4 mt-5">
              <button
                onClick={onClose}
                disabled={saving}
                className="rounded-lg border border-[#252a38] px-4 py-2 text-[12px] font-bold text-[#94a3b8] transition-colors hover:bg-[#1e2235] disabled:opacity-50"
              >
                Hủy
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !value.trim()}
                className="flex items-center gap-1.5 rounded-lg bg-[#3b82f6] px-5 py-2 text-[12px] font-bold text-white transition-colors hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                {saving ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ─── Proxy Row ──────────────────────────────────────────────────────────────

interface ProxyRowProps {
  proxy: IProxy;
  checkingId: string | null;
  onDelete: (id: string) => Promise<boolean>;
  onCheckHealth: (value: string) => void;
  onEdit: (proxy: IProxy) => void;
}

function ProxyRow({ proxy, checkingId, onDelete, onCheckHealth, onEdit }: ProxyRowProps) {
  const [showFull, setShowFull] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const accountName =
    (proxy.accountId as IAccount | null)?.username ?? '-';
  const masked = maskProxy(proxy.value);
  const alive = proxy.alive;
  const lastChecked = proxy.lastChecked;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(proxy.value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy proxy:', err);
    }
  };

  const handleConfirmDelete = async () => {
    if (!proxy._id || deleting) return;
    setDeleting(true);
    const ok = await onDelete(proxy._id);
    setDeleting(false);
    if (!ok) setConfirmDelete(false);
  };

  return (
    <tr className="border-t border-[#1e2235] transition-colors hover:bg-[#1c2030]">
      {/* TRẠNG THÁI */}
      <td className="w-[110px] px-5 py-3">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold">
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              alive === true
                ? 'bg-[#22c55e] shadow-[0_0_6px_rgba(34,197,94,0.4)]'
                : alive === false
                  ? 'bg-[#ef4444]'
                  : 'bg-[#475569]'
            }`}
          />
          <span
            className={
              alive === true
                ? 'text-[#22c55e]'
                : alive === false
                  ? 'text-[#ef4444]'
                  : 'text-[#64748b]'
            }
          >
            {alive === true ? 'Live' : alive === false ? 'Dead' : 'Chưa check'}
          </span>
        </span>
      </td>

      {/* PROXY */}
      <td className="px-5 py-3">
        <div className="flex items-center gap-2">
          <code className="font-mono text-[12px] text-[#7dd3fc]">
            {showFull ? proxy.value : masked}
          </code>
          <button
            type="button"
            onClick={() => setShowFull((v) => !v)}
            title={showFull ? 'Ẩn' : 'Hiện đầy đủ'}
            className="flex items-center rounded p-0.5 text-[#334155] transition-all hover:bg-[#1e2235] hover:text-[#7dd3fc]"
          >
            {showFull ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
          <button
            type="button"
            onClick={handleCopy}
            title="Copy"
            className="flex items-center rounded p-0.5 text-[#334155] transition-all hover:bg-[#1e2235] hover:text-[#7dd3fc]"
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
          </button>
        </div>
      </td>

      {/* TÀI KHOẢN */}
      <td className="px-5 py-3">
        <span className="inline-flex items-center gap-1.5 rounded bg-[#1e2235] px-2.5 py-0.5 text-[12px] text-[#94a3b8]">
          <User size={11} />
          {accountName}
        </span>
      </td>

      {/* LẦN CUỐI CHECK */}
      <td className="px-5 py-3">
        <span className="text-[11px] text-[#64748b]">
          {lastChecked ? formatTime(lastChecked) : 'Chưa check'}
        </span>
      </td>

      {/* ACTION */}
      <td className="w-[130px] px-5 py-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onCheckHealth(proxy.value)}
            disabled={checkingId !== null}
            title="Kiểm tra proxy"
            className="flex items-center rounded p-1 text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#22c55e] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {checkingId === proxy.value ? (
              <Activity size={14} className="animate-spin text-[#3b82f6]" />
            ) : (
              <Activity size={14} />
            )}
          </button>
          <button
            type="button"
            onClick={() => onEdit(proxy)}
            title="Sửa proxy"
            className="flex items-center rounded p-1 text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#3b82f6]"
          >
            <Pencil size={14} />
          </button>
          {confirmDelete ? (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="rounded border border-[#7f1d1d] bg-[#450a0a] px-2 py-0.5 text-[11px] font-bold text-[#ef4444] transition-colors hover:bg-[#7f1d1d] disabled:opacity-50"
              >
                {deleting ? '...' : 'Xóa'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="rounded border border-[#252a38] bg-[#1e2235] px-2 py-0.5 text-[11px] font-bold text-[#64748b] transition-colors hover:bg-[#252a35] disabled:opacity-50"
              >
                Hủy
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              title="Xoá"
              className="flex items-center rounded p-1 text-[#64748b] transition-colors hover:bg-[#252a35] hover:text-[#ef4444]"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Proxy Page ─────────────────────────────────────────────────────────────

export const Proxy: React.FC = () => {
  const {
    proxies,
    loading,
    submitting,
    checking,
    checkingId,
    addManyProxies,
    updateProxy,
    deleteProxy,
    checkAllHealth,
    checkSingleHealth,
    page,
    totalPages,
    total,
    search,
    setPage,
    setSearch,
  } = useProxies();
  const [searchInput, setSearchInput] = useState(search);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [editProxy, setEditProxy] = useState<IProxy | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(1);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pagination pages
  const pageNumbers: number[] = [];
  if (totalPages > 1) {
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    for (let i = 0; i < 5; i++) {
      const p = start + i;
      if (p > totalPages) break;
      pageNumbers.push(p);
    }
  }

  return (
    <div className="space-y-4 text-[#e2e8f0]">
      {/* Proxy List Card */}
      <div className="overflow-hidden rounded-xl border border-[#252a38] bg-[#161920]">
        <DataToolbar
          icon={<Shield size={16} />}
          title="Danh sách Proxies"
          count={total}
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Tìm kiếm proxy..."
          action={
            <>
              <button
                type="button"
                onClick={() => setShowAddPopup(true)}
                className="ml-2 flex items-center gap-1 rounded-lg bg-[#3b82f6] px-3 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-[#2563eb]"
              >
                <Plus size={13} />
                Thêm
              </button>
              <button
                type="button"
                onClick={checkAllHealth}
                disabled={checking || total === 0}
                className="ml-2 flex items-center gap-1.5 rounded-lg border border-[#166534] bg-[#14532d] px-3 py-1.5 text-[12px] font-semibold text-[#22c55e] transition-all hover:bg-[#166534] hover:text-[#4ade80] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Activity size={14} className={checking ? 'animate-spin' : ''} />
                {checking ? 'Đang check...' : 'Health Check All'}
              </button>
            </>
          }
        />

        <DataStateMessage
          loading={loading}
          empty={!loading && proxies.length === 0 ? (search ? 'Không tìm thấy proxy phù hợp' : 'Chưa có proxy nào') : null}
          emptyHint={
            !loading && proxies.length === 0
              ? search
                ? 'Thử tìm kiếm với từ khóa khác'
                : 'Thêm proxy ở ô bên trên để bắt đầu'
              : undefined
          }
          emptyIcon={<Shield size={32} className="text-[#252a38]" />}
        />

        {!loading && proxies.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-[#10131a]">
                  <th className="px-5 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                    Trạng thái
                  </th>
                  <th className="px-5 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                    Proxy
                  </th>
                  <th className="px-5 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                    Tài khoản
                  </th>
                  <th className="px-5 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                    Lần cuối
                  </th>
                  <th className="px-5 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {proxies.map((proxy) => (
                  <ProxyRow
                    key={proxy._id ?? proxy.value}
                    proxy={proxy}
                    checkingId={checkingId}
                    onDelete={deleteProxy}
                    onCheckHealth={checkSingleHealth}
                    onEdit={setEditProxy}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination — proxy giữ riêng (có page numbers) vì layout khác account */}
        <div className="flex items-center justify-between border-t border-[#252a38] px-5 py-3">
          <span className="text-[12px] text-[#64748b]">
            Tổng: <strong className="text-[#e2e8f0]">{total}</strong> proxy
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
              className="flex items-center gap-1 rounded-lg border border-[#252a38] bg-[#0d0f14] px-3 py-1.5 text-[12px] font-bold text-[#94a3b8] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft size={14} />
              <span className="hidden sm:inline">Trước</span>
            </button>

            {pageNumbers.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`min-w-[32px] rounded-lg px-2.5 py-1.5 text-[12px] font-bold transition-colors ${
                  p === page
                    ? 'bg-[#3b82f6] text-white'
                    : 'border border-[#252a38] bg-[#0d0f14] text-[#94a3b8] hover:bg-[#1e2235] hover:text-[#e2e8f0]'
                }`}
              >
                {p}
              </button>
            ))}

            <button
              type="button"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
              className="flex items-center gap-1 rounded-lg border border-[#252a38] bg-[#0d0f14] px-3 py-1.5 text-[12px] font-bold text-[#94a3b8] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span className="hidden sm:inline">Sau</span>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Add Popup */}
      <ProxyAddPopup
        isOpen={showAddPopup}
        onClose={() => setShowAddPopup(false)}
        onSubmit={addManyProxies}
      />

      {/* Edit Popup */}
      {editProxy && (
        <ProxyEditPopup
          isOpen={!!editProxy}
          onClose={() => setEditProxy(null)}
          proxy={editProxy}
          onSave={updateProxy}
        />
      )}
    </div>
  );
};
